import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Pie, PieChart, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import firebaseApp, { storage } from "../../config/firebase";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import freightService from "../../service/freight.service";
import userService from "../../service/user.service";
import "./styles.css";

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const DEFAULT_MONTH_GOAL = 450000;
const db = firebaseApp.firestore();

/** Início (00:00) e fim (23:59:59.999) do mês calendário (ano 1–12). */
function getMonthInclusiveRange(year, month) {
  const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const end = new Date(year, month, 0, 23, 59, 59, 999);
  return { start, end };
}

function toDateSafe(rawDate) {
  if (!rawDate) return null;
  let d = null;
  if (rawDate?.toDate) {
    d = rawDate.toDate();
  } else if (typeof rawDate === "string") {
    const trimmed = rawDate.trim();
    // Treat YYYY-MM-DD as local calendar date to avoid UTC shift.
    const localDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (localDateMatch) {
      const year = Number(localDateMatch[1]);
      const monthIndex = Number(localDateMatch[2]) - 1;
      const day = Number(localDateMatch[3]);
      d = new Date(year, monthIndex, day, 0, 0, 0, 0);
    } else {
      d = new Date(rawDate);
    }
  } else {
    d = new Date(rawDate);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function parseMoney(raw) {
  if (raw === undefined || raw === null || raw === "") return 0;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  const source = String(raw).trim().replace(/\s/g, "");
  const normalized = source.includes(",")
    ? source.replace(/\./g, "").replace(",", ".")
    : source;
  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatBrl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
}

function formatPtBrNumber(value) {
  const numeric = Math.max(0, parseMoney(value));
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function getWeekRange(referenceDate) {
  const day = referenceDate.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const start = new Date(referenceDate);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - diffToMonday);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

function isPendingStatus(statusDescribe) {
  const s = normalizeText(statusDescribe);
  return s === "pendente de contratacao" || s === "pendente contratacao";
}

function isContractedStatus(statusDescribe) {
  return normalizeText(statusDescribe) === "contratado";
}

function isDeliveredOrFinalized(statusDescribe) {
  const s = normalizeText(statusDescribe);
  return s === "entregue" || s === "finalizado";
}

function isInTransit(statusDescribe) {
  return normalizeText(statusDescribe) === "em transito";
}

function isCanceled(statusDescribe) {
  const s = normalizeText(statusDescribe);
  return s.includes("cancelad");
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function inRange(date, start, end) {
  return date >= start && date <= end;
}

function getFirstPickupOperationDate(doc) {
  const rawDate =
    doc?.freight?.firstDelivery?.dateColeta ??
    doc?.firstDelivery?.dateColeta

  return toDateSafe(rawDate);
}

function buildYearOptions(freightDocs) {
  const years = new Set([new Date().getFullYear()]);
  (freightDocs || []).forEach((doc) => {
    const data = doc.data ? doc.data() : doc;
    const d = getFirstPickupOperationDate(data);
    if (d) {
      years.add(d.getFullYear());
    }
  });
  return Array.from(years).sort((a, b) => b - a);
}

function getFirstStop(stops = []) {
  if (!Array.isArray(stops) || stops.length === 0) {
    return null;
  }
  return [...stops].sort((a, b) => (a?.stop_order || 9999) - (b?.stop_order || 9999))[0] || null;
}

function isPickupStop(stop) {
  if (!stop) return false;
  const operation = Array.isArray(stop.operation) ? stop.operation.map((op) => normalizeText(op)) : [];
  const typeOperation = normalizeText(stop.typeOperation);
  return operation.includes("carga");
}

function isPickupFinished(stop) {
  if (!stop) return false;
  const loadingStatus = String(stop.loadingStatus || "").toUpperCase();
  return stop.concluded === true || loadingStatus === "LOADING_COMPLETED";
}

function isTransitPendingAtPickup(stop) {
  if (!stop) return false;
  const loadingStatus = String(stop.loadingStatus || "").toUpperCase();
  const stopText = normalizeText(
    stop.loadingStatusDescription || stop.loadingStatusLabel || stop.status || ""
  );
  return (
    loadingStatus === "HEADING_TO_PICKUP" ||
    loadingStatus === "WAITING_LOADING" ||
    loadingStatus === "WAITING_DOCUMENTATION" ||
    stopText === "deslocando-se para a coleta" ||
    stopText === "deslocando-se para coleta" ||
    stopText === "aguardando carregamento" ||
    stopText === "aguardando documentacao"
  );
}

function isDeliveryStop(stop) {
  if (!stop) return false;
  const operation = Array.isArray(stop.operation) ? stop.operation.map((op) => normalizeText(op)) : [];
  const typeOperation = normalizeText(stop.typeOperation);
  return operation.includes("descarga") || typeOperation === "unloading";
}

function getFirstDeliveryStop(stops = []) {
  if (!Array.isArray(stops) || stops.length === 0) {
    return null;
  }
  return (
    [...stops]
      .sort((a, b) => (a?.stop_order || 9999) - (b?.stop_order || 9999))
      .find((stop) => isDeliveryStop(stop)) || null
  );
}

function getFirstPickupStop(stops = []) {
  if (!Array.isArray(stops) || stops.length === 0) {
    return null;
  }
  return (
    [...stops]
      .sort((a, b) => (a?.stop_order || 9999) - (b?.stop_order || 9999))
      .find((stop) => isPickupStop(stop)) || null
  );
}

function isInTransitToDeliveryOrWaitingUnloading(stop) {
  if (!stop) return false;
  const unloadingStatus = String(stop.unloadingStatus || "").toUpperCase();
  const stopText = normalizeText(
    stop.unloadingStatusDescription || stop.unloadingStatusLabel || stop.status || ""
  );
  return (
    unloadingStatus === "IN_TRANSIT" ||
    unloadingStatus === "WAITING_UNLOADING" ||
    stopText.includes("indo para descarga") ||
    stopText.includes("aguardando descarga") ||
    stopText.includes("aguardando descarregamento")
  );
}

function isCreatedAndNotDeliveredOrFinalized(statusDescribe) {
  const s = normalizeText(statusDescribe);
  return s === "criado" && !isDeliveredOrFinalized(statusDescribe);
}

function isPickupLoadingCompletedStatus(stop) {
  if (!stop) return false;
  const loadingStatus = String(stop.loadingStatus || "").toUpperCase();
  const stopText = normalizeText(
    stop.loadingStatusDescription || stop.loadingStatusLabel || stop.status || ""
  );
  return loadingStatus === "LOADING_COMPLETED" || stopText.includes("etapa de carregamento finalizada");
}

function isDeliveryWaitingOrCompletedUnloading(stop) {
  if (!stop) return false;
  const unloadingStatus = String(stop.unloadingStatus || "").toUpperCase();
  const stopText = normalizeText(
    stop.unloadingStatusDescription || stop.unloadingStatusLabel || stop.status || ""
  );
  return (
    unloadingStatus === "WAITING_UNLOADING" ||
    unloadingStatus === "UNLOADING_COMPLETED" ||
    stopText.includes("aguardando descarregamento") ||
    stopText.includes("etapa de descarregamento finalizada")
  );
}

function qualifiesMonthlyCardRevenueStatus(doc) {
  const statusDescribe = doc?.status?.describe || "";
  if (
    isInTransit(statusDescribe) ||
    isDeliveredOrFinalized(statusDescribe) ||
    isCreatedAndNotDeliveredOrFinalized(statusDescribe)
  ) {
    return true;
  }

  const firstPickupStop = getFirstPickupStop(doc?.stopping_points);
  if (isPickupLoadingCompletedStatus(firstPickupStop) || isPickupFinished(firstPickupStop)) {
    return true;
  }

  const firstDeliveryStop = getFirstDeliveryStop(doc?.stopping_points);
  return isDeliveryWaitingOrCompletedUnloading(firstDeliveryStop);
}

function getFreightSellerName(doc) {
  return String(doc?.freight?.seller || "").trim();
}

function getFreightSellerId(doc) {
  return String(doc?.freight?.sellerId || "").trim();
}

function getSellerGoalKey(row) {
  const sellerId = String(row?.sellerId || "").trim();
  if (sellerId) return sellerId;
  return normalizeText(row?.name || "");
}

function sanitizeFieldKey(value) {
  return String(value || "").replace(/[.#$/\[\]]/g, "_");
}

function getSellerGoalKeysFromRow(row) {
  const idKey = row?.sellerId ? sanitizeFieldKey(String(row.sellerId).trim()) : null;
  const nameKeyRaw = normalizeText(row?.name || "");
  const nameKey = nameKeyRaw ? sanitizeFieldKey(nameKeyRaw) : null;
  return { idKey, nameKey };
}

function calculateMonthlyRevenueValue(docs, rangeStart, rangeEnd) {
  return (docs || []).reduce((acc, doc) => {

    const pickupAt = getFirstPickupOperationDate(doc);
    if (!pickupAt || !inRange(pickupAt, rangeStart, rangeEnd)) {
      return acc;
    }
    const amount = parseMoney(doc?.freight?.valueFreightage);
    return qualifiesMonthlyCardRevenueStatus(doc) ? acc + amount : acc;
  }, 0);
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/** Interseção da semana (seg–dom) com o mês selecionado — ex.: mês começa na quarta, a “semana” no mês tem só os dias que caem dentro do mês. */
function clipWeekToMonth(weekStart, weekEnd, year, month) {
  const monthStart = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
  const start = weekStart < monthStart ? monthStart : weekStart;
  const end = weekEnd > monthEnd ? monthEnd : weekEnd;
  if (start > end) return null;
  return { start, end };
}

function countInclusiveCalendarDays(start, end) {
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((e - s) / (24 * 60 * 60 * 1000)) + 1;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Fanfare curto via Web Audio (fallback se não houver arquivo em /winner.mp3). */
function playWinnerFanfareWebAudio() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      const t0 = now + i * 0.11;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.11, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, t0 + 0.32);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.35);
    });
    setTimeout(() => {
      ctx.close().catch(() => {});
    }, 900);
  } catch {
    /* ignore */
  }
}

/** Som de “vitória”: tenta `/public/winner.mp3`; se falhar, usa fanfare sintético. */
function playPodiumWinnerSound() {
  try {
    const audio = new Audio(`${process.env.PUBLIC_URL || ""}/winner.mp3`);
    audio.volume = 0.35;
    const p = audio.play();
    if (p !== undefined) {
      p.catch(() => {
        playWinnerFanfareWebAudio();
      });
    }
  } catch {
    playWinnerFanfareWebAudio();
  }
}

/**
 * Som ao bater a meta mensal (fogos). Tenta `goal-reached.mp3` em public/;
 * se não existir ou falhar o play, usa o mesmo fluxo do pódio (`winner.mp3`).
 */
function playRevenueGoalCelebrationSound() {
  const base = process.env.PUBLIC_URL || "";
  try {
    const primary = new Audio(`${base}/goal-reached.mp3`);
    primary.volume = 0.38;
    const p = primary.play();
    if (p !== undefined) {
      p.catch(() => {
        playPodiumWinnerSound();
      });
    }
  } catch {
    playPodiumWinnerSound();
  }
}

const REVENUE_GOAL_FIREWORKS_MS = 5000;
/** Fundo verde “piscando” após bater a meta (1 hora). */
const REVENUE_GOAL_BG_FLASH_MS = 60 * 60 * 1000;

function createFireworkBurstBatch(batchSize, batchKey) {
  return Array.from({ length: batchSize }, (_, i) => {
    const particleCount = 14 + Math.floor(Math.random() * 12);
    const dist = 48 + Math.random() * 92;
    const particles = [];
    for (let p = 0; p < particleCount; p += 1) {
      const angle = (Math.PI * 2 * p) / particleCount + (Math.random() - 0.5) * 0.35;
      particles.push({
        id: `${i}-${p}`,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist,
        hue: 160 + Math.floor(Math.random() * 200),
        delay: Math.random() * 0.2,
      });
    }
    return {
      id: `burst-${batchKey}-${i}`,
      leftPct: 4 + Math.random() * 92,
      topPct: 3 + Math.random() * 58,
      particles,
    };
  });
}

/** Fogos em tela cheia (CSS) — ondas de estouros enquanto `visible` (ex.: 5s). */
function DashboardFireworksOverlay({ visible }) {
  const [bursts, setBursts] = useState([]);

  useEffect(() => {
    if (!visible) {
      setBursts([]);
      return;
    }

    const batchKey0 = `${Date.now()}-0`;
    setBursts(createFireworkBurstBatch(12, batchKey0));

    let tick = 0;
    const intervalMs = 380;
    const intervalId = setInterval(() => {
      tick += 1;
      const batchKey = `${Date.now()}-${tick}`;
      setBursts((prev) => {
        const next = [...prev, ...createFireworkBurstBatch(6, batchKey)];
        return next.length > 120 ? next.slice(-120) : next;
      });
    }, intervalMs);

    return () => {
      clearInterval(intervalId);
      setBursts([]);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div className="sales-fireworks-overlay" aria-hidden>
      {bursts.map((b) => (
        <div
          key={b.id}
          className="sales-fireworks-burst"
          style={{
            left: `${b.leftPct}%`,
            top: `${b.topPct}%`,
          }}
        >
          {b.particles.map((p) => (
            <span
              key={`${b.id}-${p.id}`}
              className="sales-fireworks-particle"
              style={{
                "--tx": `${p.tx}px`,
                "--ty": `${p.ty}px`,
                animationDelay: `${p.delay}s`,
                backgroundColor: `hsl(${p.hue}, 88%, 58%)`,
                boxShadow: `0 0 7px 2px hsla(${p.hue}, 88%, 58%, 0.85)`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function extractPhotoCandidate(rawPhoto) {
  if (!rawPhoto) return "";
  if (typeof rawPhoto === "string") return rawPhoto.trim();
  if (typeof rawPhoto === "object") {
    const candidate =
      rawPhoto.url ||
      rawPhoto.downloadURL ||
      rawPhoto.downloadUrl ||
      rawPhoto.file ||
      rawPhoto.path ||
      rawPhoto.fullPath ||
      "";
    return String(candidate || "").trim();
  }
  return "";
}

function extractStoragePathFromFirebaseUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url);
    const isFirebaseHost = parsed.hostname.includes("firebasestorage.googleapis.com");
    if (!isFirebaseHost) return "";
    const marker = "/o/";
    const idx = parsed.pathname.indexOf(marker);
    if (idx < 0) return "";
    const encodedPath = parsed.pathname.slice(idx + marker.length);
    return decodeURIComponent(encodedPath);
  } catch {
    return "";
  }
}

/** Avatar para pódio (maior) ou tabela (menor). */
function SellerAvatar({ name, photoURL, size = "table" }) {
  const isPodium = size === "podium";
  const imgClass = isPodium ? "sales-avatar sales-avatar--podium sales-avatar--img" : "sales-rank__avatar sales-rank__avatar--img";
  const fallbackClass = isPodium ? "sales-avatar sales-avatar--podium" : "sales-rank__avatar";
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState("");
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const candidate = extractPhotoCandidate(photoURL);

    if (!candidate) {
      setResolvedPhotoUrl("");
      return () => {
        cancelled = true;
      };
    }

    const normalized = candidate.toLowerCase();
    const isDirectUrl =
      normalized.startsWith("http://") ||
      normalized.startsWith("https://") ||
      normalized.startsWith("data:image/");

    const firebasePathFromUrl = extractStoragePathFromFirebaseUrl(candidate);
    if (isDirectUrl && !firebasePathFromUrl) {
      setResolvedPhotoUrl(candidate);
      return () => {
        cancelled = true;
      };
    }

    const storagePathRaw = firebasePathFromUrl || candidate;
    const storagePath = storagePathRaw.startsWith("/") ? storagePathRaw.slice(1) : storagePathRaw;
    getDownloadURL(storageRef(storage, storagePath))
      .then((url) => {
        if (!cancelled) {
          setResolvedPhotoUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedPhotoUrl(isDirectUrl ? candidate : "");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [photoURL]);

  useEffect(() => {
    setImgFailed(false);
  }, [resolvedPhotoUrl]);

  if (resolvedPhotoUrl && !imgFailed) {
    return <img className={imgClass} src={resolvedPhotoUrl} alt="" onError={() => setImgFailed(true)} />;
  }
  return (
    <div className={fallbackClass} aria-hidden>
      {getInitials(name)}
    </div>
  );
}

function DashboardVendedores() {
  const userLogged = useSelector((state) => state.usuarioLogado);
  const user = useSelector((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [freightDocs, setFreightDocs] = useState([]);
  const [sellersList, setSellersList] = useState([]);
  const [metaMes2FreightValue, setMetaMes2FreightValue] = useState(0);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedWeekDate, setSelectedWeekDate] = useState(formatDateInput(now));
  const [sellerGoalsByKey, setSellerGoalsByKey] = useState({});
  const [editingGoalKey, setEditingGoalKey] = useState(null);
  const [revenueGoalFireworksVisible, setRevenueGoalFireworksVisible] = useState(false);
  const [goalDashboardBgFlash, setGoalDashboardBgFlash] = useState(false);
  const sellersListRef = useRef([]);

  const filterMonthRange = useMemo(
    () => getMonthInclusiveRange(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );
  const goalDocId = useMemo(
    () => `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`,
    [selectedYear, selectedMonth]
  );

  const shipperIds = useMemo(
    () => userService.getShipperIdsFromUser(user),
    [user?.uidShipper, user?.uidShipperAffiliates]
  );
  const shipperScopeKey = shipperIds.join(",");

  useEffect(() => {
    if (!shipperIds.length) return;
    setLoading(true);

    let active = true;
    let sellersLoaded = false;
    let freightsLoaded = false;

    const freightDataById = new Map();
    const stopsByFreightId = new Map();
    const stopUnsubByFreightId = new Map();

    const setDashboardState = () => {
      if (!active || !sellersLoaded || !freightsLoaded) {
        return;
      }

      const docsWithStops = Array.from(freightDataById.entries()).map(([id, data]) => ({
        id,
        ...data,
        stopping_points: stopsByFreightId.get(id) || [],
      }));

      setFreightDocs(docsWithStops);
      setLoading(false);
    };

    const clearStopListener = (freightId) => {
      const stopUnsub = stopUnsubByFreightId.get(freightId);
      if (stopUnsub) stopUnsub();
      stopUnsubByFreightId.delete(freightId);
      stopsByFreightId.delete(freightId);
    };

    userService
      .getSellersByShipperIds(shipperIds)
      .then((sellers) => {
        if (!active) return;
        const sellerUsers = (sellers || []).filter((seller) => Boolean(seller?.isSeller));
        setSellersList(sellerUsers);
        sellersLoaded = true;
        setDashboardState();
      })
      .catch((error) => {
        console.error("Erro ao buscar vendedores do dashboard:", error);
        if (!active) return;
        sellersLoaded = true;
        setSellersList([]);
        setDashboardState();
      });

    const freightsUnsubscribe = freightService.subscribeAllFreightsByShipperIds(
      shipperIds,
      (freightsSnapshot) => {
        if (!active) return;
        const currentIds = new Set();

        (freightsSnapshot.docs || []).forEach((freightDoc) => {
          const freightId = freightDoc.id;
          currentIds.add(freightId);
          freightDataById.set(freightId, freightDoc.data());
          if (!stopsByFreightId.has(freightId)) {
            stopsByFreightId.set(freightId, []);
          }

          if (!stopUnsubByFreightId.has(freightId)) {
            const stopUnsubscribe = freightService.subscribeStoppingPointsByFreight(
              freightId,
              (stopsSnapshot) => {
                if (!active) return;
                const stops = (stopsSnapshot.docs || []).map((sp) => ({
                  id: sp.id,
                  ...sp.data(),
                }));
                stopsByFreightId.set(freightId, stops);
                setDashboardState();
              },
              (error) => {
                console.error("Erro em listener de stopping_points:", freightId, error);
                if (!active) return;
                stopsByFreightId.set(freightId, []);
                setDashboardState();
              }
            );
            stopUnsubByFreightId.set(freightId, stopUnsubscribe);
          }
        });

        Array.from(freightDataById.keys()).forEach((freightId) => {
          if (!currentIds.has(freightId)) {
            freightDataById.delete(freightId);
            clearStopListener(freightId);
          }
        });

        freightsLoaded = true;
        setDashboardState();
      },
      (error) => {
        console.error("Erro em listener de fretes do dashboard:", error);
        if (!active) return;
        freightsLoaded = true;
        setDashboardState();
      }
    );

    return () => {
      active = false;
      freightsUnsubscribe && freightsUnsubscribe();
      Array.from(stopUnsubByFreightId.values()).forEach((unsub) => unsub && unsub());
      stopUnsubByFreightId.clear();
    };
  }, [shipperScopeKey]);

  useEffect(() => {

    const { start: rangeStart, end: rangeEnd } = filterMonthRange;
    var retotno = calculateMonthlyRevenueValue(freightDocs, rangeStart, rangeEnd);
    const monthlyRevenueFreights = (freightDocs || []).filter((doc) => {
      const pickupAt = getFirstPickupOperationDate(doc);
      if (!pickupAt || !inRange(pickupAt, rangeStart, rangeEnd)) {
        return false;
      }
      return qualifiesMonthlyCardRevenueStatus(doc);
    });

    setMetaMes2FreightValue(retotno);
  }, [freightDocs, filterMonthRange]);

  const referenceDate = useMemo(() => {
    const d = new Date(selectedWeekDate);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [selectedWeekDate]);

  const weekRange = useMemo(() => getWeekRange(referenceDate), [referenceDate]);
  const weekRangeInMonth = useMemo(
    () =>
      clipWeekToMonth(
        weekRange.start,
        weekRange.end,
        selectedYear,
        selectedMonth
      ),
    [weekRange.start, weekRange.end, selectedYear, selectedMonth]
  );
  const years = useMemo(() => buildYearOptions(freightDocs), [freightDocs]);

  const sellerByName = useMemo(() => {
    const map = {};
    (sellersList || []).forEach((s) => {
      const n = String(s?.name || "").trim();
      if (n) map[normalizeText(n)] = s;
    });
    return map;
  }, [sellersList]);

  useEffect(() => {
    sellersListRef.current = sellersList || [];
  }, [sellersList]);

  useEffect(() => {
    if (!user?.uidShipper) {
      setSellerGoalsByKey({});
      return;
    }

    const unsubscribe = db
      .collection("shipper")
      .doc(user.uidShipper)
      .collection("dashboard_seller_goals")
      .doc(goalDocId)
      .onSnapshot(
        (docSnapshot) => {
          const data = docSnapshot.exists ? docSnapshot.data() : {};
          const goals = data?.goals || {};
          const normalizedGoals = {};
          Object.keys(goals).forEach((key) => {
            normalizedGoals[key] = Math.max(0, parseMoney(goals[key]));
          });
          setSellerGoalsByKey(() => {
            const next = { ...normalizedGoals };
            (sellersListRef.current || []).forEach((seller) => {
              const idKey = seller?.id ? sanitizeFieldKey(String(seller.id).trim()) : null;
              const nameKeyRaw = normalizeText(seller?.name || "");
              const nameKey = nameKeyRaw ? sanitizeFieldKey(nameKeyRaw) : null;

              if (!idKey && !nameKey) return;

              const idVal = idKey ? next[idKey] : undefined;
              const nameVal = nameKey ? next[nameKey] : undefined;

              // Propaga valor entre as duas chaves quando existir só uma delas
              if (idKey && nameVal === undefined && idVal !== undefined) next[nameKey] = idVal;
              if (nameKey && idVal === undefined && nameVal !== undefined) next[idKey] = nameVal;

              if (idKey && next[idKey] === undefined) next[idKey] = 0;
              if (nameKey && next[nameKey] === undefined) next[nameKey] = 0;
            });
            return next;
          });
        },
        (error) => {
          console.error("Erro ao carregar metas dos vendedores:", error);
          setSellerGoalsByKey({});
        }
      );

    return () => unsubscribe && unsubscribe();
  }, [user?.uidShipper, goalDocId]);

  useEffect(() => {
    setSellerGoalsByKey((prev) => {
      const next = { ...prev };
      let changed = false;
      (sellersList || []).forEach((seller) => {
        const idKey = seller?.id ? sanitizeFieldKey(String(seller.id).trim()) : null;
        const nameKeyRaw = normalizeText(seller?.name || "");
        const nameKey = nameKeyRaw ? sanitizeFieldKey(nameKeyRaw) : null;

        if (idKey && next[idKey] === undefined) {
          next[idKey] = 0;
          changed = true;
        }
        if (nameKey && next[nameKey] === undefined) {
          next[nameKey] = 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sellersList]);

  const monthGoal = useMemo(() => {
    return (sellersList || []).reduce((acc, seller) => {
      const idKey = seller?.id ? sanitizeFieldKey(String(seller.id).trim()) : null;
      const nameKeyRaw = normalizeText(seller?.name || "");
      const nameKey = nameKeyRaw ? sanitizeFieldKey(nameKeyRaw) : null;
      const rawValue =
        (idKey && sellerGoalsByKey[idKey] !== undefined ? sellerGoalsByKey[idKey] : undefined) ??
        (nameKey ? sellerGoalsByKey[nameKey] : undefined) ??
        0;
      return acc + parseMoney(rawValue);
    }, 0);
  }, [sellersList, sellerGoalsByKey]);

  const handleSellerMonthGoalChange = (row, value) => {
    const key = sanitizeFieldKey(row?.goalKey || getSellerGoalKey(row));
    if (!key) return;
    setEditingGoalKey(key);
    setSellerGoalsByKey((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const saveSellerMonthGoal = (row, rawValue) => {
    const numericValue = Math.max(0, parseMoney(rawValue));
    const { idKey, nameKey } = getSellerGoalKeysFromRow(row);
    const hasAnyKey = Boolean(idKey || nameKey);
    if (!hasAnyKey) return;
    const formattedValue = formatPtBrNumber(numericValue);

    setSellerGoalsByKey((prev) => ({
      ...prev,
      ...(idKey ? { [idKey]: formattedValue } : {}),
      ...(nameKey ? { [nameKey]: formattedValue } : {}),
    }));
    setEditingGoalKey(null);
    if (!user?.uidShipper) return;

    const nestedGoals = {};
    if (idKey) nestedGoals[idKey] = numericValue;
    if (nameKey) nestedGoals[nameKey] = numericValue;

    db.collection("shipper")
      .doc(user.uidShipper)
      .collection("dashboard_seller_goals")
      .doc(goalDocId)
      .set(
        {
          year: selectedYear,
          month: selectedMonth,
          updatedAt: new Date(),
          goals: nestedGoals,
        },
        { merge: true }
      )
      .catch((error) => {
        console.error("Erro ao salvar meta do vendedor:", error);
      });
  };

  useEffect(() => {
    const { start: rangeStart, end: rangeEnd } = filterMonthRange;
    const freightsInFixedRange = (freightDocs || []).filter((doc) => {
      const pickupAt = getFirstPickupOperationDate(doc);
      if (!pickupAt) return false;
      return inRange(pickupAt, rangeStart, rangeEnd);
    });

    const csvEscape = (value) => {
      const s = String(value ?? "");
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csvHeader = "numeroFrete;valorFrete;status;dataColeta";
    const csvLines = freightsInFixedRange.map((doc) => {
      const pickupAt = getFirstPickupOperationDate(doc);
      const numeroFrete =
        doc?.numberSerial ??
        doc?.freight?.numberSerial ??
        doc?.freight?.freightNumber ??
        doc?.freightNumber ??
        doc?.id ??
        "Sem numero";
      const valorFrete = doc?.freight?.valueFreightage ?? doc?.valueFreightage ?? 0;
      const status = doc?.status?.describe ?? doc?.status?.code ?? "Sem status";
      const dataColeta = pickupAt ? pickupAt.toLocaleDateString("pt-BR") : "Sem data";
      return [numeroFrete, valorFrete, status, dataColeta].map(csvEscape).join(";");
    });

    
  }, [freightDocs, filterMonthRange]);

  const computed = useMemo(() => {
    const { start: rangeStart, end: rangeEnd } = filterMonthRange;
    const allFreights = freightDocs || [];

    const monthFreights = allFreights.filter((doc) => {
      const pickupAt = getFirstPickupOperationDate(doc);
      if (!pickupAt) return false;
      return inRange(pickupAt, rangeStart, rangeEnd);
    });

    let weeklyBilling = 0;
    let daySales = 0;
    let pendingContractValue = 0;
    let pendingValue = 0;
    const activeFreightCount = monthFreights.filter(
      (doc) => !isPendingStatus(doc?.status?.describe || "") && !isCanceled(doc?.status?.describe || "")
    ).length;

    const sellerById = {};
    const sellerByNormalizedName = {};
    (sellersList || []).forEach((seller) => {
      const id = String(seller?.id || "").trim();
      const name = String(seller?.name || "").trim();
      if (id) sellerById[id] = seller;
      if (name) sellerByNormalizedName[normalizeText(name)] = seller;
    });

    const sellerMap = {};
    (sellersList || []).forEach((seller) => {
      const id = String(seller?.id || "").trim();
      const name = String(seller?.name || "").trim();
      const key = id || normalizeText(name);
      if (!key) return;
      sellerMap[key] = {
        name: name || key,
        sellerId: id || null,
        salesDay: 0,
        salesWeek: 0,
        salesMonth: 0,
        soldFreights: 0,
        pendingTotal: 0,
      };
    });

    const ensureSellerRow = (freightSellerId, freightSellerName) => {
      const id = String(freightSellerId || "").trim();
      const name = String(freightSellerName || "").trim();
      const meta =
        (id && sellerById[id]) ||
        (name && sellerByNormalizedName[normalizeText(name)]) ||
        null;

      if (!meta) {
        return null;
      }

      const metaId = String(meta.id || "").trim();
      const metaName = String(meta.name || "").trim();
      const key = metaId || normalizeText(metaName);
      if (!key) return null;

      if (!sellerMap[key]) {
        sellerMap[key] = {
          name: metaName || key,
          sellerId: metaId || null,
          salesDay: 0,
          salesWeek: 0,
          salesMonth: 0,
          soldFreights: 0,
          pendingTotal: 0,
        };
      }
      return sellerMap[key];
    };

    monthFreights.forEach((doc) => {
      const freight = doc?.freight || {};
      const statusDescribe = doc?.status?.describe || "";
      const pickupAt = getFirstPickupOperationDate(doc);
      const firstStop = getFirstStop(doc?.stopping_points);
      const firstStopIsPickup = isPickupStop(firstStop);
      const firstStopTransitPending = isTransitPendingAtPickup(firstStop);
      const amount = parseMoney(freight?.valueFreightage);
      const sellerRow = ensureSellerRow(
        getFreightSellerId(doc),
        getFreightSellerName(doc),
      );

      if (!pickupAt) return;

      if (isPendingStatus(statusDescribe)) {
        pendingContractValue += amount;
        if (sellerRow) sellerRow.pendingTotal += amount;
      }

      if (
        isContractedStatus(statusDescribe) ||
        (isInTransit(statusDescribe) && firstStopIsPickup && firstStopTransitPending)
      ) {
        pendingValue += amount;
        if (sellerRow) sellerRow.pendingTotal += amount;
      }

      const qualifiesMonthlyBilling = qualifiesMonthlyCardRevenueStatus(doc);

      if (qualifiesMonthlyBilling && sellerRow) {
        sellerRow.salesMonth += amount;
        sellerRow.soldFreights += 1;
      }

      if (
        qualifiesMonthlyBilling &&
        sellerRow &&
        isSameDay(pickupAt, referenceDate)
      ) {
        daySales += amount;
        sellerRow.salesDay += amount;
      }
    });

    allFreights.forEach((doc) => {
      const pickupAt = getFirstPickupOperationDate(doc);
      if (!pickupAt) {
        return;
      }
      if (!inRange(pickupAt, weekRange.start, weekRange.end)) {
        return;
      }
      const qualifiesWeeklyBilling = qualifiesMonthlyCardRevenueStatus(doc);

      if (!qualifiesWeeklyBilling) {
        return;
      }

      const amount = parseMoney(doc?.freight?.valueFreightage);
      weeklyBilling += amount;

      const sellerRow = ensureSellerRow(
        getFreightSellerId(doc),
        getFreightSellerName(doc),
      );
      if (!sellerRow) {
        return;
      }
      sellerRow.salesWeek += amount;
    });

    const ranking = Object.values(sellerMap).sort((a, b) => {
        if (b.salesMonth !== a.salesMonth) return b.salesMonth - a.salesMonth;
        if (b.salesWeek !== a.salesWeek) return b.salesWeek - a.salesWeek;
        return b.salesDay - a.salesDay;
      });

    const rankingWithGoals = ranking.map((item) => {
      const key = normalizeText(item.name);
      const sellerMeta =
        (item.sellerId && sellerById[item.sellerId]) ||
        sellerByName[key] ||
        null;
      const sellerGoalKey = sanitizeFieldKey(
        getSellerGoalKey({
          sellerId: item.sellerId || sellerMeta?.id,
          name: item.name,
        })
      );
      const rawGoal = sellerGoalsByKey[sellerGoalKey];
      const monthGoalInput =
        rawGoal === undefined || rawGoal === null
          ? ""
          : editingGoalKey === sellerGoalKey
          ? String(rawGoal)
          : formatPtBrNumber(rawGoal);
      return {
        ...item,
        goalKey: sellerGoalKey,
        monthGoalInput,
        monthGoal: parseMoney(rawGoal),
        sellerId: item.sellerId || sellerMeta?.id || null,
        photoURL: sellerMeta?.photoURL || sellerMeta?.photoUrl || sellerMeta?.file || null,
      };
    });

    const monthDays = daysInMonth(selectedYear, selectedMonth);
    const daysInWeekWindow =
      weekRangeInMonth != null
        ? countInclusiveCalendarDays(weekRangeInMonth.start, weekRangeInMonth.end)
        : 0;
    const weeklyGoalFromMonth =
      monthDays > 0 && daysInWeekWindow > 0
        ? (monthGoal / monthDays) * daysInWeekWindow
        : 0;
    const weeklyGoalGap = weeklyGoalFromMonth - weeklyBilling;
    const pendingAndOpenValue = pendingContractValue + pendingValue;

    return {
      weeklyBilling,
      pendingContractValue,
      pendingValue,
      weeklyGoalFromMonth,
      weeklyGoalGap,
      activeFreightCount,
      pendingAndOpenValue,
      daySales,
      ranking: rankingWithGoals,
      weekDaysInMonth: daysInWeekWindow,
    };
  }, [
    freightDocs,
    filterMonthRange,
    selectedYear,
    selectedMonth,
    monthGoal,
    weekRange.start,
    weekRange.end,
    referenceDate,
    sellersList,
    sellerByName,
    sellerGoalsByKey,
  ]);

  const billingOverMetaPercentLabel =
    monthGoal > 0 ? (metaMes2FreightValue / monthGoal) * 100 : 0;
  const billingOverMetaPercent = Math.max(0, Math.min(100, billingOverMetaPercentLabel));

  const pendingOverMetaPercentLabel =
    monthGoal > 0 ? (computed.pendingAndOpenValue / monthGoal) * 100 : 0;
  const pendingOverMetaPercent = Math.max(0, Math.min(100, pendingOverMetaPercentLabel));

  // Mantém a escala em 0–100 sem “terceira cor” visível.
  const remainingPercent = Math.max(0, 100 - billingOverMetaPercent - pendingOverMetaPercent);

  const monthFlowGaugeData = [
    { name: "Faturamento mensal / Meta", value: billingOverMetaPercent },
    { name: "Pendências / Meta", value: pendingOverMetaPercent },
    { name: "Restante", value: remainingPercent },
  ];

  const podium = computed.ranking.slice(0, 3);
  const topOne = podium[0];
  const topTwo = podium[1];
  const topThree = podium[2];

  const prevFirstPlaceKeyRef = useRef(null);
  const fireworksHideTimerRef = useRef(null);
  const goalDashboardBgTimerRef = useRef(null);
  const prevMonthlyRevenueMetGoalRef = useRef(null);

  useEffect(() => {
    prevMonthlyRevenueMetGoalRef.current = null;
    if (goalDashboardBgTimerRef.current) {
      clearTimeout(goalDashboardBgTimerRef.current);
      goalDashboardBgTimerRef.current = null;
    }
    setGoalDashboardBgFlash(false);
  }, [selectedYear, selectedMonth, monthGoal]);

  useEffect(() => {
    if (loading) return;
    const firstPlaceKey = topOne
      ? String(topOne.sellerId || "").trim() || normalizeText(topOne.name || "")
      : null;

    const prev = prevFirstPlaceKeyRef.current;
    if (prev != null && firstPlaceKey != null && firstPlaceKey !== prev) {
      playPodiumWinnerSound();
    }
    prevFirstPlaceKeyRef.current = firstPlaceKey;
  }, [loading, topOne?.sellerId, topOne?.name]);

  useEffect(() => {
    if (loading) return;
    const goalPositive = monthGoal > 0;
    const metGoal = goalPositive && metaMes2FreightValue >= monthGoal;
    const prevMet = prevMonthlyRevenueMetGoalRef.current;

    if (prevMet === null) {
      prevMonthlyRevenueMetGoalRef.current = metGoal;
      return;
    }
    if (!prevMet && metGoal) {
      playRevenueGoalCelebrationSound();
      setRevenueGoalFireworksVisible(true);
      if (fireworksHideTimerRef.current) {
        clearTimeout(fireworksHideTimerRef.current);
      }
      fireworksHideTimerRef.current = setTimeout(() => {
        setRevenueGoalFireworksVisible(false);
        fireworksHideTimerRef.current = null;
      }, REVENUE_GOAL_FIREWORKS_MS);
      if (goalDashboardBgTimerRef.current) {
        clearTimeout(goalDashboardBgTimerRef.current);
        goalDashboardBgTimerRef.current = null;
      }
      setGoalDashboardBgFlash(true);
      goalDashboardBgTimerRef.current = setTimeout(() => {
        setGoalDashboardBgFlash(false);
        goalDashboardBgTimerRef.current = null;
      }, REVENUE_GOAL_BG_FLASH_MS);
    }
    prevMonthlyRevenueMetGoalRef.current = metGoal;
  }, [loading, metaMes2FreightValue, monthGoal]);

  useEffect(
    () => () => {
      if (fireworksHideTimerRef.current) {
        clearTimeout(fireworksHideTimerRef.current);
      }
      if (goalDashboardBgTimerRef.current) {
        clearTimeout(goalDashboardBgTimerRef.current);
      }
    },
    []
  );

  if (userLogged <= 0) {
    return <Navigate to="/login" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <div
          className={
            goalDashboardBgFlash
              ? "sales-dashboard sales-dashboard--goal-bg-flash"
              : "sales-dashboard"
          }
        >
          <DashboardFireworksOverlay visible={revenueGoalFireworksVisible} />
          <div className="sales-dashboard__topbar">
            <div className="sales-dashboard__header">
              <h2>
                {goalDashboardBgFlash
                  ? "Meta do mês batida! Obrigado Fortianos!!!!"
                  : "Dashboard Comercial - Vendedores"}
              </h2>
            </div>
            <div className="sales-dashboard__filters sales-dashboard__filters--inline">
              <div className="sales-filter-field">
                <label htmlFor="sales-year">Ano</label>
                <select
                  id="sales-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sales-filter-field">
                <label htmlFor="sales-month">Mês</label>
                <select
                  id="sales-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sales-filter-field">
                <label htmlFor="sales-week-date">Semana</label>
                <input
                  id="sales-week-date"
                  type="date"
                  value={selectedWeekDate}
                  onChange={(e) => setSelectedWeekDate(e.target.value)}
                />
              </div>

              <div className="sales-filter-field">
                <label htmlFor="month-goal">Meta mês (R$)</label>
                <input
                  id="month-goal"
                  type="text"
                  value={formatPtBrNumber(monthGoal)}
                  readOnly
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="sales-dashboard__loading">
              <span className="spinner-border text-danger" role="status" />
            </div>
          ) : (
            <>
              <section className="sales-dashboard__main-grid">
                <div className="sales-panel sales-panel--cards-block">
                  {/* <h4>Cards</h4> */}
                  <section className="sales-dashboard__cards">
                    <article className="sales-card sales-card--blue">
                      <h5>Meta Mensal</h5>
                      <strong>{formatBrl(monthGoal)}</strong>
                    </article>
                    <article className="sales-card sales-card--blue">
                      <h5>Faturamento mensal</h5>
                      <strong>{formatBrl(metaMes2FreightValue)}</strong>
                    </article>
                    <article className="sales-card" style={{ backgroundColor: "#F4BF00" }}>
                      <h5>Pendente de contratação</h5>
                      <strong>{formatBrl(computed.pendingContractValue)}</strong>
                    </article>
                    <article className="sales-card" style={{ backgroundColor: "#F4BF00" }}>
                      <h5>Pendentes</h5>
                      <strong>{formatBrl(computed.pendingValue)}</strong>
                    </article>
                    <article className="sales-card sales-card--red">
                      <h5>Meta Semanal</h5>
                      <strong>{formatBrl(computed.weeklyGoalFromMonth)}</strong>
                      {computed.weekDaysInMonth > 0 && (
                        <small className="sales-card__hint">
                          proporcional a {computed.weekDaysInMonth} dia(s) no mês
                        </small>
                      )}
                    </article>
                    <article className="sales-card sales-card--red">
                      <h5>Faturamento Semanal</h5>
                      <strong>{formatBrl(computed.weeklyBilling)}</strong>
                    </article>
                    <article className="sales-card sales-card--red">
                      <h5>Gap Semanal</h5>
                      <strong>{formatBrl(computed.weeklyGoalGap)}</strong>
                    </article>
                    <article className="sales-card sales-card--blue">
                      <h5>Qtd. Fretes Ativos</h5>
                      <strong>{computed.activeFreightCount}</strong>
                    </article>
                  </section>
                </div>

                <div className="sales-panel sales-panel--podium-block">
                  <h4>Pódio do Mês</h4>
                  <div className="sales-podium">
                    <div className="sales-podium__col">
                      <div className="sales-podium__avatar-wrap">
                        <SellerAvatar
                          name={topTwo?.name}
                          photoURL={topTwo?.photoURL}
                          size="podium"
                        />
                      </div>
                      <div className="sales-podium__bar sales-podium__bar--second">
                        <span>2º</span>
                        <small>{topTwo?.name || "-"}</small>
                        <strong>{formatBrl(topTwo?.salesMonth || 0)}</strong>
                      </div>
                    </div>
                    <div className="sales-podium__col sales-podium__col--first">
                      <div className="sales-podium__avatar-wrap">
                        <SellerAvatar
                          name={topOne?.name}
                          photoURL={topOne?.photoURL}
                          size="podium"
                        />
                      </div>
                      <div className="sales-podium__bar sales-podium__bar--first">
                        <span>1º</span>
                        <small>{topOne?.name || "-"}</small>
                        <strong>{formatBrl(topOne?.salesMonth || 0)}</strong>
                      </div>
                    </div>
                    <div className="sales-podium__col">
                      <div className="sales-podium__avatar-wrap">
                        <SellerAvatar
                          name={topThree?.name}
                          photoURL={topThree?.photoURL}
                          size="podium"
                        />
                      </div>
                      <div className="sales-podium__bar sales-podium__bar--third">
                        <span>3º</span>
                        <small>{topThree?.name || "-"}</small>
                        <strong>{formatBrl(topThree?.salesMonth || 0)}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sales-panel sales-panel--gauge-right">
                  <h4>Faturamento mensal x Pendências</h4>
                  <div className="sales-gauge">
                    <div className="sales-gauge__percents" aria-hidden>
                      <span className="sales-gauge__percent sales-gauge__percent--green">
                        {`${billingOverMetaPercentLabel.toFixed(1)}%`}
                      </span>
                      <span className="sales-gauge__percent sales-gauge__percent--yellow">
                        {`${pendingOverMetaPercentLabel.toFixed(1)}%`}
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={monthFlowGaugeData}
                          dataKey="value"
                          cx="50%"
                          cy="85%"
                          startAngle={180}
                          endAngle={0}
                          innerRadius={78}
                          outerRadius={110}
                          stroke="none"
                        >
                          <Cell fill="#2e7d32" />
                          <Cell fill="#f4bf00" />
                          <Cell fill="transparent" />
                        </Pie>
                        <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="sales-gauge__legend">
                    <span>
                      <i className="dot dot--green" />
                      Faturamento mensal / Meta: {formatBrl(metaMes2FreightValue)} /{" "}
                      {formatBrl(monthGoal)}
                    </span>
                    <span>
                      <i className="dot dot--yellow" />
                      Pendências / Meta: {formatBrl(computed.pendingAndOpenValue)} /{" "}
                      {formatBrl(monthGoal)}
                    </span>
                  </div>
                </div>
              </section>

              <section className="sales-panel sales-panel--ranking-block sales-panel--ranking-full">
                <h4>Ranking de Vendedores</h4>
                <div className="sales-table-wrapper">
                  <table className="sales-table">
                    <thead>
                      <tr>
                        <th>Ranking</th>
                        <th>Vendedor</th>
                        <th>Venda Dia</th>
                        <th>Venda Semana</th>
                        <th>Venda Mês</th>
                        <th>Pendente</th>
                        <th>Meta Mês</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computed.ranking.length === 0 ? (
                        <tr>
                          <td colSpan={7}>Nenhuma venda encontrada para os filtros.</td>
                        </tr>
                      ) : (
                        computed.ranking.map((row, idx) => (
                          <tr key={`${row.name}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="sales-rank__user">
                                <SellerAvatar name={row.name} photoURL={row.photoURL} size="table" />
                                <span className="sales-rank__name">{row.name}</span>
                              </div>
                            </td>
                            <td>{formatBrl(row.salesDay)}</td>
                            <td>{formatBrl(row.salesWeek)}</td>
                            <td>{formatBrl(row.salesMonth)}</td>
                            <td>{formatBrl(row.pendingTotal)}</td>
                            <td>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={row.monthGoalInput}
                                onChange={(e) => handleSellerMonthGoalChange(row, e.target.value)}
                                onBlur={(e) => saveSellerMonthGoal(row, e.target.value)}
                                style={{ width: "120px" }}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      }
    />
  );
}

export default DashboardVendedores;
